import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { SectionLabel, useCustomerFetch } from "../../components/customer-app/ui";
import {
  Home, Building2, Building, Zap, Lightbulb, BatteryCharging, HelpCircle,
  ChevronLeft, Loader2, PartyPopper, User, Users, MapPin, Plus, Check, Hash, ShoppingCart,
} from "lucide-react";

const TYPES = [
  { key: "residential", label: "Home", icon: Home },
  { key: "commercial", label: "Business", icon: Building2 },
  { key: "housing_society", label: "Housing Society", icon: Building },
  { key: "ev_charger", label: "EV Charger", icon: Zap },
  { key: "street_light", label: "Street Light", icon: Lightbulb },
  { key: "battery_storage", label: "Battery Storage", icon: BatteryCharging },
  { key: "others", label: "Others", icon: HelpCircle },
];
const EPC_TYPES = new Set(["residential", "commercial", "housing_society"]);
const START_CATEGORY_KEYS = new Set(TYPES.map((t) => t.key));

export default function CustomerBuyInstallation() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { data: me } = useCustomerFetch("/customer-app/me");
  const [mode, setMode] = useState(params.get("mode") === "refer" ? "refer" : "self");
  const [customerType, setCustomerType] = useState(
    START_CATEGORY_KEYS.has(params.get("category")) ? params.get("category") : "residential"
  );
  const [existingCustomerId, setExistingCustomerId] = useState(null);
  const [addingNewAddress, setAddingNewAddress] = useState(false);
  const [address, setAddress] = useState("");
  const [pinCode, setPinCode] = useState("");
  const [friendName, setFriendName] = useState("");
  const [friendPhone, setFriendPhone] = useState("");
  const [friendPin, setFriendPin] = useState("");
  const [friendAddress, setFriendAddress] = useState("");
  const [usage, setUsage] = useState(300);
  const [notes, setNotes] = useState("");
  const [otherText, setOtherText] = useState("");
  const [referralCode, setReferralCode] = useState(() => localStorage.getItem("purvodaya_ref_code") || "");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isEpc = EPC_TYPES.has(customerType);
  const addresses = me?.customers || [];

  function switchMode(m) {
    setMode(m);
    setExistingCustomerId(null);
    setAddingNewAddress(m === "refer" || addresses.length === 0);
  }

  async function submit(e) {
    e.preventDefault();
    if (mode === "self" && !existingCustomerId && (!address.trim() || !/^\d{6}$/.test(pinCode))) {
      toast.error("Address & a valid 6-digit PIN code are required"); return;
    }
    if (mode === "refer" && (!friendName.trim() || !friendPhone.trim() || !/^\d{6}$/.test(friendPin))) {
      toast.error("Friend's name, phone & a valid 6-digit PIN code are required"); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        mode,
        customer_type: customerType,
        lead_category: customerType,
        lead_category_other_text: customerType === "others" ? otherText : undefined,
        avg_power_consumption: isEpc ? Number(usage) : 0,
        notes,
      };
      if (mode === "self") {
        if (existingCustomerId) payload.existing_customer_id = existingCustomerId;
        else { payload.address = address; payload.pin_code = pinCode; }
        payload.referral_code = referralCode || undefined;
      } else {
        payload.friend_name = friendName;
        payload.friend_phone = friendPhone;
        payload.friend_pin_code = friendPin;
        payload.friend_address = friendAddress;
      }
      const { data } = await api.post("/customer-app/leads", payload);
      if (mode === "self" && referralCode) {
        if (data.referral_applied) toast.success("Referral code applied!");
        else toast.info("Referral code not recognised — request submitted anyway.");
      }
      localStorage.removeItem("purvodaya_ref_code");
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  }

  if (done) {
    return (
      <div className="pb-4 text-center pt-10" data-testid="buy-installation-success">
        <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
          <PartyPopper className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-extrabold tracking-tight text-slate-900 mt-4">Request received!</h2>
        <p className="text-sm text-slate-500 mt-2 px-4">
          {mode === "refer" ? "We'll reach out to your friend shortly — you can track their progress from your profile." : "Our solar experts will call you shortly to plan your new installation."}
        </p>
        <button onClick={() => nav("/app")} data-testid="buy-installation-done-home"
          className="mt-6 bg-[#0D4721] text-white px-6 py-3 rounded-xl text-sm font-bold">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="pb-4" data-testid="customer-buy-installation">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-xs font-bold text-slate-500 mb-3">
        <ChevronLeft className="w-3.5 h-3.5" /> Back
      </button>
      <h2 className="text-xl font-extrabold tracking-tight text-slate-900 flex items-center gap-2">
        <ShoppingCart className="w-5 h-5 text-[#0D4721]" /> New Order
      </h2>
      <p className="text-sm text-slate-500 mt-1">A quick note is all we need — our team handles the rest.</p>

      <div className="grid grid-cols-2 gap-2 mt-4">
        <button type="button" onClick={() => switchMode("self")} data-testid="buy-mode-self"
          className={"flex items-center justify-center gap-1.5 py-3 rounded-xl border-2 text-sm font-bold transition-colors " + (mode === "self" ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500")}>
          <User className="w-4 h-4" /> For Myself
        </button>
        <button type="button" onClick={() => switchMode("refer")} data-testid="buy-mode-refer"
          className={"flex items-center justify-center gap-1.5 py-3 rounded-xl border-2 text-sm font-bold transition-colors " + (mode === "refer" ? "border-amber-500 bg-amber-50 text-amber-700" : "border-slate-200 text-slate-500")}>
          <Users className="w-4 h-4" /> Refer for Other
        </button>
      </div>

      <form onSubmit={submit} className="space-y-5 mt-5">
        <div>
          <SectionLabel className="mb-2">What are you interested in?</SectionLabel>
          <div className="grid grid-cols-2 gap-2">
            {TYPES.map((t) => (
              <button key={t.key} type="button" onClick={() => setCustomerType(t.key)} data-testid={`buy-type-${t.key}`}
                className={"flex flex-col items-center gap-1.5 py-3 rounded-xl border-2 transition-colors " + (customerType === t.key ? "border-[#0D4721] bg-emerald-50 text-[#0D4721]" : "border-slate-200 text-slate-500")}>
                <t.icon className="w-4.5 h-4.5" />
                <span className="text-[11px] font-semibold text-center leading-tight">{t.label}</span>
              </button>
            ))}
          </div>
          <div className={"mt-2 inline-flex px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider " + (isEpc ? "bg-sky-50 text-sky-700" : "bg-purple-50 text-purple-700")}>
            {isEpc ? "Full Rooftop Installation" : "Product Enquiry"}
          </div>
        </div>

        {customerType === "others" && (
          <div>
            <SectionLabel className="mb-2">Tell us what you need</SectionLabel>
            <textarea value={otherText} onChange={(e) => setOtherText(e.target.value)} required rows={2}
              placeholder="e.g. Solar water heater, off-grid inverter…"
              data-testid="buy-other-text"
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400" />
          </div>
        )}

        {mode === "self" ? (
          <div>
            <SectionLabel className="mb-2">Installation address</SectionLabel>
            {addresses.length > 0 && (
              <div className="space-y-2 mb-2">
                {addresses.map((c) => (
                  <button
                    type="button"
                    key={c.id}
                    onClick={() => { setExistingCustomerId(c.id); setAddingNewAddress(false); }}
                    data-testid={`buy-address-option-${c.id}`}
                    className={"w-full flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-colors " + (existingCustomerId === c.id ? "border-[#0D4721] bg-emerald-50" : "border-slate-200")}
                  >
                    <MapPin className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold text-slate-800 truncate">{c.address || c.customer_name}</div>
                      {c.pin_code && <div className="text-xs text-slate-500 font-mono mt-0.5 flex items-center gap-1"><Hash className="w-3 h-3" /> {c.pin_code}</div>}
                    </div>
                    {existingCustomerId === c.id && <Check className="w-4 h-4 text-[#0D4721] flex-shrink-0" />}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => { setAddingNewAddress(true); setExistingCustomerId(null); }}
                  data-testid="buy-add-new-address-btn"
                  className={"w-full flex items-center gap-2 p-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-colors " + (addingNewAddress ? "border-[#0D4721] text-[#0D4721] bg-emerald-50" : "border-slate-300 text-slate-500")}
                >
                  <Plus className="w-4 h-4" /> Add a new address
                </button>
              </div>
            )}
            {addingNewAddress && (
              <div className="space-y-2">
                <textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2}
                  placeholder="Where should we install your solar system?"
                  data-testid="buy-address"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400" />
                <input
                  inputMode="numeric" maxLength={6} value={pinCode}
                  onChange={(e) => setPinCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="PIN Code *" data-testid="buy-pincode"
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm font-mono outline-none focus:border-emerald-400" />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3 bg-amber-50/60 border border-amber-200 rounded-xl p-3.5">
            <SectionLabel className="text-amber-700">Your friend's details</SectionLabel>
            <input value={friendName} onChange={(e) => setFriendName(e.target.value)} placeholder="Friend's full name *"
              data-testid="buy-friend-name" className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-amber-400" />
            <input type="tel" inputMode="numeric" value={friendPhone} onChange={(e) => setFriendPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Friend's phone number *" data-testid="buy-friend-phone"
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm font-mono outline-none focus:border-amber-400" />
            <textarea value={friendAddress} onChange={(e) => setFriendAddress(e.target.value)} rows={2}
              placeholder="Friend's address" data-testid="buy-friend-address"
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-amber-400" />
            <input inputMode="numeric" maxLength={6} value={friendPin} onChange={(e) => setFriendPin(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Friend's PIN Code *" data-testid="buy-friend-pin"
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm font-mono outline-none focus:border-amber-400" />
          </div>
        )}

        {isEpc && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <SectionLabel>Monthly electricity usage</SectionLabel>
              <span className="text-sm font-bold text-[#0D4721] font-mono">{usage} units</span>
            </div>
            <input type="range" min={50} max={2000} step={50} value={usage} onChange={(e) => setUsage(e.target.value)}
              data-testid="buy-usage-slider" className="w-full accent-[#0D4721]" />
            <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-1"><span>50</span><span>2000+</span></div>
          </div>
        )}

        <div>
          <SectionLabel className="mb-2">Anything else? <span className="text-slate-400">(optional)</span></SectionLabel>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
            placeholder="Roof size, budget, preferred timeline…"
            data-testid="buy-notes"
            className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm outline-none focus:border-emerald-400" />
        </div>

        {mode === "self" && (
          <div>
            <SectionLabel className="mb-2">Referral code <span className="text-slate-400">(optional)</span></SectionLabel>
            <input type="text" value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              placeholder="Got a code from a friend?"
              data-testid="buy-referral-code"
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-3 text-sm font-mono tracking-widest outline-none focus:border-emerald-400" />
          </div>
        )}

        <button type="submit" disabled={submitting} data-testid="buy-submit"
          className="w-full bg-gradient-to-br from-amber-500 to-amber-600 text-white py-4 rounded-xl text-sm uppercase tracking-wider font-bold flex items-center justify-center gap-2 disabled:opacity-40">
          {submitting && <Loader2 className="w-4 h-4 animate-spin" />} {mode === "refer" ? "Submit Referral" : "Get a Free Quote"}
        </button>
      </form>
    </div>
  );
}
